/**
 * Canonical trust & limits copy — the single source every page quotes
 * for upload limits, data retention, and AI-training statements (same
 * pattern as `usePricingCopy()` for prices), so the story can never
 * drift between surfaces again.
 *
 * Ground truth these values mirror:
 * - Upload caps: `_MAX_BYTES_BY_KIND` in backend `routes/uploads.py`
 *   (denial 10 MB, medical record 75 MB) and the page-count gate
 *   (`upload_max_pages_medical_record`, 400).
 * - Retention: backend `scripts/purge_session_data.py` sweeps all
 *   session data older than 24 hours; nothing is kept longer.
 * - Download links: delivery links expire 24 hours after the
 *   delivery email is sent.
 */

export const DENIAL_MAX_MB = 10;
export const MEDICAL_RECORD_MAX_MB = 75;
export const MEDICAL_RECORD_MAX_PAGES = 400;
export const RETENTION_HOURS = 24;
export const DOWNLOAD_LINK_HOURS = 24;

// Pasted-denial bounds, mirroring the backend /uploads/denial-text route.
export const PASTED_TEXT_MIN_CHARS = 80;
export const PASTED_TEXT_MAX_CHARS = 100_000;

export const DENIAL_UPLOAD_HINT = `PDF, JPG, or PNG — up to ${DENIAL_MAX_MB} MB`;
export const DENIAL_ACCEPT_TYPES = "application/pdf,image/jpeg,image/png";
export const MEDICAL_RECORD_UPLOAD_HINT =
  `PDF only, up to ${MEDICAL_RECORD_MAX_MB} MB / ${MEDICAL_RECORD_MAX_PAGES} pages`;

/**
 * The one retention story. Uploads, extracted data, drafts, and the
 * transcript are all deleted within RETENTION_HOURS on a fixed server
 * schedule; closing the tab / starting over clears the browser
 * immediately but server-side deletion is the sweep, not the click
 * (that's what keeps a paid download link valid for its full window).
 */
export const RETENTION_STATEMENT =
  `Your uploads, the data extracted from them, your drafted appeal, and the chat ` +
  `transcript are automatically deleted within ${RETENTION_HOURS} hours, on a fixed ` +
  `schedule. Closing the tab or starting over clears your browser right away; ` +
  `anything on our servers is removed by that same sweep.`;

export const DOWNLOAD_LINK_STATEMENT =
  `Download links stay valid for ${DOWNLOAD_LINK_HOURS} hours after delivery.`;

/**
 * Documents never train AI; anonymized outcome data (did the appeal
 * succeed) is the only thing we learn from. Every surface that touches
 * training must state BOTH halves — saying only one reads as a
 * contradiction of the other.
 */
export const TRAINING_STATEMENT =
  `Your documents and letters are never used to train AI — ours or anyone ` +
  `else's. The only thing we learn from is anonymized outcome data (whether an ` +
  `appeal succeeded), which contains no documents and no identifying details.`;
